/**
 * Hardened fetch wrapper with automatic CSRF token handling and 419 recovery
 * Use this for all non-Inertia fetch requests that need CSRF protection
 */

import { toast } from 'sonner';
import { getCsrfToken, refreshCsrfToken } from './csrf';

/**
 * Global flag to prevent multiple simultaneous refresh attempts
 */
let isRefreshing = false;
let refreshPromise: Promise<string> | null = null;

/**
 * Handle 419 CSRF token mismatch errors
 * @param response The failed response
 * @param originalRequest The original request options
 * @returns A new response after refresh/retry, or null if refresh failed
 */
async function handle419Error(response: Response, originalRequest: RequestInit): Promise<Response | null> {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing && refreshPromise) {
        await refreshPromise;
        // Retry the original request with the new token
        const retryToken = getCsrfToken();
        const retryHeaders = new Headers(originalRequest.headers);
        retryHeaders.set('X-CSRF-TOKEN', retryToken);
        retryHeaders.set('X-XSRF-TOKEN', retryToken);
        return fetch(response.url, {
            ...originalRequest,
            headers: retryHeaders,
        });
    }

    isRefreshing = true;
    refreshPromise = refreshCsrfToken();

    try {
        await refreshPromise;
        toast.info('Session refreshed. Retrying...');

        // Retry the original request with the new token
        const retryToken = getCsrfToken();
        const retryHeaders = new Headers(originalRequest.headers);
        retryHeaders.set('X-CSRF-TOKEN', retryToken);
        retryHeaders.set('X-XSRF-TOKEN', retryToken);
        return fetch(response.url, {
            ...originalRequest,
            headers: retryHeaders,
        });
    } catch {
        // Refresh failed - user needs to reload
        toast.error('Session expired. Please refresh the page.');
        window.location.reload();
        return null;
    } finally {
        isRefreshing = false;
        refreshPromise = null;
    }
}

/**
 * Hardened fetch wrapper with automatic CSRF token handling
 *
 * @param url The URL to fetch
 * @param options Fetch options (will be enhanced with CSRF headers)
 * @returns A Promise that resolves to the Response
 *
 * @example
 * ```ts
 * const response = await csrfFetch('/api/endpoint', {
 *   method: 'POST',
 *   body: JSON.stringify({ data: 'value' }),
 * });
 * ```
 */
export async function csrfFetch(url: string | URL | Request, options: RequestInit = {}): Promise<Response> {
    // Get current CSRF token
    const csrfToken = getCsrfToken();

    // Merge headers, ensuring CSRF token and required headers are set
    const headers = new Headers(options.headers);
    headers.set('X-CSRF-TOKEN', csrfToken);
    headers.set('X-XSRF-TOKEN', csrfToken); // Laravel also accepts X-XSRF-TOKEN from XSRF-TOKEN cookie
    headers.set('X-Requested-With', 'XMLHttpRequest');

    // Only set Accept if not already set
    if (!headers.has('Accept')) {
        headers.set('Accept', 'application/json');
    }

    // Build the request with enhanced options
    const requestOptions: RequestInit = {
        ...options,
        headers,
        credentials: 'same-origin',
    };

    // Make the request
    let response = await fetch(url, requestOptions);

    // Handle 419 CSRF token mismatch
    if (response.status === 419) {
        const retryResponse = await handle419Error(response, requestOptions);
        if (retryResponse) {
            response = retryResponse;
        } else {
            // Refresh failed, page will reload
            throw new Error('Session expired');
        }
    }

    return response;
}
